import { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TopRankedHero from './components/TopRankedHero';
import RankingTabs, { type RankMode } from './components/RankingTabs';
import RankedStoryCard, { type RankedStory } from './components/RankedStoryCard';
import TopWritersSidebar from './components/TopWritersSidebar';
import { supabase } from '../../lib/supabase';
import SEOHead from '../../components/feature/SEOHead';

const PAGE_SIZE = 20;

const TopRankedPage = () => {
  const [mode, setMode] = useState<RankMode>('liked');
  const [genreFilter, setGenreFilter] = useState('All');
  const [stories, setStories] = useState<RankedStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchStories = useCallback(
    async (currentPage: number, reset: boolean) => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('get_ranked_stories', {
          p_mode:   mode,
          p_genre:  genreFilter,
          p_limit:  PAGE_SIZE,
          p_offset: (currentPage - 1) * PAGE_SIZE,
        });

        if (error) throw error;

        const enriched: RankedStory[] = (data || []).map((s: any) => ({
          id:             s.id,
          title:          s.title,
          genre:          s.genre,
          content:        s.content,
          cover_image:    s.cover_image,
          like_count:     s.like_count,
          read_count:     s.read_count,
          published_date: s.published_date,
          profiles:       { display_name: s.display_name },
          _likeCount:     Number(s.real_like_count),
          _commentCount:  Number(s.comment_count),
          _avgRating:     s.avg_rating !== null ? Number(s.avg_rating) : null,
        }));

        setStories((prev) => (reset ? enriched : [...prev, ...enriched]));
        setHasMore((data || []).length === PAGE_SIZE);
      } catch (err) {
        console.error('Error fetching ranked stories:', err);
      } finally {
        setLoading(false);
      }
    },
    [mode, genreFilter]
  );

  // Reset and refetch when mode or genre changes
  useEffect(() => {
    setPage(1);
    fetchStories(1, true);
  }, [mode, genreFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchStories(next, false);
  };

  const modeLabel: Record<RankMode, string> = {
    liked: 'Most Liked',
    read:  'Most Read',
    rated: 'Top Rated',
  };

  const modeIcon: Record<RankMode, string> = {
    liked: 'ri-heart-fill text-red-500',
    read:  'ri-eye-fill text-orange-400',
    rated: 'ri-star-fill text-yellow-500',
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="Top Ranked Horror Stories | Most Liked & Most Read – Sinister Files"
        description="Discover the best horror stories on Sinister Files ranked by likes, reads, and ratings. The most-loved dark fiction, creepypasta, and supernatural horror tales curated by the community."
        canonical="https://sinisterfiles.com/top-ranked"
      />
      <Navbar />

      <TopRankedHero />

      <RankingTabs
        active={mode}
        onChange={(m) => setMode(m)}
        genreFilter={genreFilter}
        onGenreChange={(g) => setGenreFilter(g)}
      />

      <main className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex gap-8">
          {/* Main list */}
          <div className="flex-1 min-w-0">
            {/* Section header */}
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-5 h-5 flex items-center justify-center ${modeIcon[mode]}`}>
                <i className={modeIcon[mode].split(' ')[0]} />
              </div>
              <h2 className="text-xl font-bold text-white font-['Cinzel']">
                {modeLabel[mode]}
              </h2>
              {genreFilter !== 'All' && (
                <span className="text-sm text-red-500 font-['Crimson_Text'] bg-red-600/10 px-2 py-0.5 rounded">
                  {genreFilter}
                </span>
              )}
            </div>

            {/* Skeleton */}
            {loading && stories.length === 0 ? (
              <div className="space-y-4">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="flex gap-4 bg-[#0f0f0f] border border-red-900/20 rounded-lg overflow-hidden animate-pulse h-28"
                  >
                    <div className="w-16 bg-gray-800 shrink-0" />
                    <div className="w-28 bg-gray-800 shrink-0" />
                    <div className="flex-1 py-4 pr-4 space-y-2">
                      <div className="h-4 bg-gray-800 rounded w-1/3" />
                      <div className="h-5 bg-gray-800 rounded w-2/3" />
                      <div className="h-3 bg-gray-800 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : stories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-red-900/10 rounded-full mb-4">
                  <i className="ri-ghost-line text-3xl text-red-800" />
                </div>
                <h3 className="text-xl font-bold text-white font-['Cinzel'] mb-2">
                  No Stories Found
                </h3>
                <p className="text-gray-500 font-['Crimson_Text'] text-sm max-w-sm">
                  No stories match the current filters. Try a different genre or ranking mode.
                </p>
                <button
                  onClick={() => setGenreFilter('All')}
                  className="mt-5 px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-['Crimson_Text'] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Clear Genre Filter
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {stories.map((story, i) => (
                    <RankedStoryCard
                      key={`${story.id}-${mode}`}
                      story={story}
                      rank={i + 1}
                      mode={mode}
                      animDelay={Math.min(i * 0.04, 0.4)}
                    />
                  ))}
                </div>

                {/* Load more */}
                {hasMore && (
                  <div className="flex justify-center mt-10">
                    <button
                      onClick={handleLoadMore}
                      disabled={loading}
                      className="px-10 py-3 bg-[#0f0f0f] border border-red-900/30 hover:border-red-600/60 text-gray-300 hover:text-white rounded-lg text-sm font-['Crimson_Text'] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 group"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <i className="ri-loader-4-line animate-spin" />
                          Loading...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <i className="ri-arrow-down-line group-hover:animate-bounce" />
                          Load More
                        </span>
                      )}
                    </button>
                  </div>
                )}

                {!hasMore && stories.length > 0 && (
                  <p className="text-center text-gray-600 text-sm font-['Crimson_Text'] mt-10">
                    You have reached the end of the rankings.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-72 shrink-0 hidden lg:block">
            <TopWritersSidebar />
          </div>
        </div>
      </main>

      <Footer />

      <style>{`
        @keyframes rankFadeIn {
          from { opacity: 0; transform: translateX(-16px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default TopRankedPage;
